// Shared PDF export pipeline — extracted from PhysicalReportExportModal.js (its original home)
// so a second page needing a multi-block, paginated A4 PDF export (GPS & Load's Week Summary)
// can reuse the exact same mechanism instead of a second implementation. Any page using this
// marks its exportable sections with `data-report-block` (and `data-force-page="true"` on a
// block that must start a fresh page), same convention PhysicalReportContent.js already uses.

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Captures every `[data-report-block]` div SEPARATELY (rather than one giant screenshot sliced
// blindly by pixel height) — each block is a self-contained unit (a whole table, a chart, a
// panel, ...) that must never be cut across a page boundary.
export async function captureBlocks(container, backgroundColor = '#ffffff', scale = 2) {
  const { default: html2canvas } = await import('html2canvas');
  const blockEls = Array.from(container.querySelectorAll('[data-report-block]'));
  const blocks = [];
  for (const el of blockEls) {
    const canvas = await html2canvas(el, { scale, useCORS: true, backgroundColor, logging: false });
    blocks.push({ canvas, forceNewPage: el.getAttribute('data-force-page') === 'true' });
  }
  return blocks;
}

// Greedily packs captured blocks onto pages: a forced-new-page block always starts a fresh
// page; otherwise a block joins the current page if it fits, else starts a new one. A block
// taller than a full page on its own falls back to being hard-sliced across its own dedicated
// pages — the only case where a block can still be split, and only when it's physically too
// long for one A4 page regardless of layout.
export function packBlocksIntoPages(blocks, pageHeightPx) {
  const pages = [];
  let current = [];
  let usedPx = 0;
  const flush = () => { if (current.length) { pages.push(current); current = []; usedPx = 0; } };

  blocks.forEach(block => {
    const h = block.canvas.height;
    if (block.forceNewPage) flush();
    if (h > pageHeightPx) {
      flush();
      let renderedPx = 0;
      while (renderedPx < h) {
        const sliceH = Math.min(pageHeightPx, h - renderedPx);
        pages.push([{ canvas: block.canvas, yOffsetPx: renderedPx, heightPx: sliceH }]);
        renderedPx += sliceH;
      }
      return;
    }
    if (usedPx + h > pageHeightPx && current.length) flush();
    current.push({ canvas: block.canvas, yOffsetPx: 0, heightPx: h });
    usedPx += h;
  });
  flush();
  return pages;
}

export function buildPageCanvas(pageEntries, canvasWidth, backgroundColor = '#ffffff') {
  const usedHeightPx = pageEntries.reduce((s, e) => s + e.heightPx, 0);
  const pageCanvas = document.createElement('canvas');
  pageCanvas.width = canvasWidth;
  pageCanvas.height = Math.max(1, usedHeightPx);
  const ctx = pageCanvas.getContext('2d');
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
  let destY = 0;
  pageEntries.forEach(entry => {
    ctx.drawImage(entry.canvas, 0, entry.yOffsetPx, entry.canvas.width, entry.heightPx, 0, destY, entry.canvas.width, entry.heightPx);
    destY += entry.heightPx;
  });
  return pageCanvas;
}

// Turns the captured+packed blocks into a real multi-page A4 PDF — one jsPDF page per packed
// page, each page's canvas built from whichever blocks were packed onto it.
// imageFormat/imageQuality/scale default to the Physical Report's original PNG-at-2x behavior
// (byte-for-byte unchanged for that caller). A page with busy, dark, gradient-heavy content —
// like Week Summary's charts, unlike the Physical Report's white-background tables — compresses
// far better as JPEG: an unparameterized PNG export of that page ran to 44MB, confirmed while
// building it; JPEG at 1.5x brings it back down to a normal export size.
export async function renderReportPdf(container, backgroundColor = '#ffffff', { scale = 2, imageFormat = 'PNG', imageQuality = 0.92 } = {}) {
  const [blocks, { jsPDF }] = await Promise.all([captureBlocks(container, backgroundColor, scale), import('jspdf')]);
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  if (!blocks.length) return pdf.output('arraybuffer');

  const pdfW = 595.28, pdfH = 841.89; // A4, points
  const margin = 24;
  const usableW = pdfW - margin * 2;
  const usableH = pdfH - margin * 2;
  const canvasWidth = blocks[0].canvas.width;
  const pxPerPt = canvasWidth / usableW;
  const pageHeightPx = Math.max(1, Math.floor(usableH * pxPerPt));

  const mime = imageFormat === 'JPEG' ? 'image/jpeg' : 'image/png';
  const pages = packBlocksIntoPages(blocks, pageHeightPx);
  pages.forEach((pageEntries, i) => {
    const pageCanvas = buildPageCanvas(pageEntries, canvasWidth, backgroundColor);
    if (i > 0) pdf.addPage();
    pdf.addImage(pageCanvas.toDataURL(mime, imageQuality), imageFormat, margin, margin, usableW, pageCanvas.height / pxPerPt);
  });
  return pdf.output('arraybuffer');
}
